import React, { useState, useEffect, useCallback } from "react";
import {
  Stock,
  StockCategory,
  StockMovement,
  StockUnit,
  StockType,
  StockMovementType,
} from "../types";
import {
  getStocks,
  getStockUnits,
  createStockUnit,
  updateStockUnit,
  deleteStockUnit,
  getStockCategories,
  createStock,
  updateStock,
  deleteStock,
  createStockCategory,
  updateStockCategory,
  deleteStockCategory,
  getStockMovements,
  getStockMovementsByStock,
  createStockMovement,
  getLowStock,
  searchStocks,
  getStocksByType,
} from "../services/api";
import { turkishIncludes } from "../utils/formatters";
import { useAuth } from "../context/AuthContext";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./StockManagement.css";

type TabType = "stocks" | "categories" | "units" | "movements" | "lowstock";

// Stok Tipi etiketleri
const stockTypeLabels: Record<StockType, string> = {
  [StockType.Hammadde]: "Hammadde",
  [StockType.YariMamul]: "Yarı Mamül",
  [StockType.Mamul]: "Mamül",
};

// Hareket Tipi etiketleri
const movementTypeLabels: Record<StockMovementType, string> = {
  [StockMovementType.Giris]: "Giriş",
  [StockMovementType.Cikis]: "Çıkış",
  [StockMovementType.Transfer]: "Transfer",
  [StockMovementType.Sayim]: "Sayım",
  [StockMovementType.Iade]: "İade",
  [StockMovementType.Fire]: "Fire",
};

// Hareket Tipi renkleri
const movementTypeColors: Record<StockMovementType, string> = {
  [StockMovementType.Giris]: "#22c55e",
  [StockMovementType.Cikis]: "#ef4444",
  [StockMovementType.Transfer]: "#3b82f6",
  [StockMovementType.Sayim]: "#f59e0b",
  [StockMovementType.Iade]: "#8b5cf6",
  [StockMovementType.Fire]: "#dc2626",
};

const StockManagement: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>("stocks");
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [categories, setCategories] = useState<StockCategory[]>([]);
  const [units, setUnits] = useState<StockUnit[]>([]);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [lowStockItems, setLowStockItems] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockTypeFilter, setStockTypeFilter] = useState<string>("all");

  // Stock Modal states
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [editingStock, setEditingStock] = useState<Stock | null>(null);
  const [stockFormData, setStockFormData] = useState({
    productCode: "",
    productName: "",
    description: "",
    categoryId: "",
    stockType: StockType.Hammadde,
    unit: "",
    currentStock: 0,
    minimumStock: 0,
    maximumStock: 0,
    unitPrice: 0,
    currency: "TRY",
    warehouseLocation: "",
    supplier: "",
    color: "",
    size: "",
    barcode: "",
  });

  // Category Modal states
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<StockCategory | null>(
    null,
  );
  const [categoryFormData, setCategoryFormData] = useState({
    name: "",
    description: "",
  });

  // Unit Modal states
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<StockUnit | null>(null);
  const [unitFormData, setUnitFormData] = useState({
    unitName: "",
    abbreviation: "",
    sortOrder: 0,
  });

  // Movement Modal states
  const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
  const [movementFormData, setMovementFormData] = useState({
    stockId: "",
    movementType: StockMovementType.Giris,
    quantity: 0,
    unitPrice: 0,
    totalPrice: 0,
    currency: "TRY",
    referenceNo: "",
    referenceType: "",
    description: "",
    movementDate: new Date().toISOString().slice(0, 16),
    movedBy: "",
    fromWarehouse: "",
    toWarehouse: "",
    relatedOrderId: undefined as number | undefined,
  });

  // Stock detail modal for viewing movements
  const [isStockDetailModalOpen, setIsStockDetailModalOpen] = useState(false);
  const [selectedStockForDetail, setSelectedStockForDetail] =
    useState<Stock | null>(null);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);

  // Alert ve Confirm Modal state'leri
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  // Data loading
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [stocksRes, categoriesRes, unitsRes] = await Promise.all([
        getStocks(),
        getStockCategories(),
        getStockUnits(),
      ]);
      setStocks(stocksRes.data || []);
      setCategories(categoriesRes.data || []);
      console.log("Units Response:", unitsRes.data);
      setUnits((unitsRes.data || []).filter((u: StockUnit) => u.isActive));
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Veriler yüklenirken hata oluştu!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMovements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getStockMovements();
      setMovements(response.data || []);
    } catch (error) {
      console.error("Hareketler yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLowStock = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getLowStock();
      setLowStockItems(response.data || []);
    } catch (error) {
      console.error("Düşük stok verileri yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === "movements") {
      loadMovements();
    } else if (activeTab === "lowstock") {
      loadLowStock();
    }
  }, [activeTab, loadMovements, loadLowStock]);

  // Search handler
  const handleSearch = async (term: string) => {
    setSearchTerm(term);
    if (term.length > 2) {
      try {
        const response = await searchStocks(term);
        setStocks(response.data || []);
      } catch (error) {
        console.error("Arama hatası:", error);
      }
    } else if (term.length === 0) {
      loadData();
    }
  };

  // Stock type filter handler
  const handleStockTypeFilter = async (type: string) => {
    setStockTypeFilter(type);
    if (type === "all") {
      loadData();
    } else {
      try {
        setLoading(true);
        const response = await getStocksByType(type);
        setStocks(response.data || []);
      } catch (error) {
        console.error("Filtreleme hatası:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  // ==================== STOCK CRUD ====================
  const openAddStockModal = () => {
    setEditingStock(null);
    setStockFormData({
      productCode: "",
      productName: "",
      description: "",
      categoryId: categories[0]?.id || "",
      stockType: StockType.Hammadde,
      unit: "adet",
      currentStock: 0,
      minimumStock: 0,
      maximumStock: 0,
      unitPrice: 0,
      currency: "TRY",
      warehouseLocation: "",
      supplier: "",
      color: "",
      size: "",
      barcode: "",
    });
    setIsStockModalOpen(true);
  };

  const openEditStockModal = (stock: Stock) => {
    setEditingStock(stock);
    setStockFormData({
      productCode: stock.productCode,
      productName: stock.productName,
      description: stock.description || "",
      categoryId: stock.categoryId,
      stockType: stock.stockType || StockType.Hammadde,
      unit: stock.unit,
      currentStock: stock.currentStock,
      minimumStock: stock.minimumStock || 0,
      maximumStock: stock.maximumStock || 0,
      unitPrice: stock.unitPrice || 0,
      currency: stock.currency || "TRY",
      warehouseLocation: stock.warehouseLocation || "",
      supplier: stock.supplier || "",
      color: stock.color || "",
      size: stock.size || "",
      barcode: stock.barcode || "",
    });
    setIsStockModalOpen(true);
  };

  const handleStockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !stockFormData.productCode.trim() ||
      !stockFormData.productName.trim()
    ) {
      setAlertModal({
        isOpen: true,
        message: "Ürün kodu ve adı zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      if (editingStock) {
        await updateStock(editingStock.id, {
          id: editingStock.id,
          ...stockFormData,
        });
        setAlertModal({
          isOpen: true,
          message: "Stok başarıyla güncellendi!",
          type: "success",
        });
      } else {
        await createStock(stockFormData);
        setAlertModal({
          isOpen: true,
          message: "Stok başarıyla oluşturuldu!",
          type: "success",
        });
      }
      setIsStockModalOpen(false);
      loadData();
    } catch (error) {
      console.error("Stok kaydedilemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Stok kaydedilirken hata oluştu!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStock = (stock: Stock) => {
    setConfirmModal({
      isOpen: true,
      message: `"${stock.productName}" stokunu silmek istediğinize emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await deleteStock(stock.id);
          setAlertModal({
            isOpen: true,
            message: "Stok başarıyla silindi!",
            type: "success",
          });
          loadData();
        } catch (error) {
          console.error("Stok silinemedi:", error);
          setAlertModal({
            isOpen: true,
            message: "Stok silinirken hata oluştu!",
            type: "error",
          });
        } finally {
          setLoading(false);
          setConfirmModal({ ...confirmModal, isOpen: false });
        }
      },
    });
  };

  // ==================== CATEGORY CRUD ====================
  const openAddCategoryModal = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: "", description: "" });
    setIsCategoryModalOpen(true);
  };

  const openEditCategoryModal = (category: StockCategory) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || "",
    });
    setIsCategoryModalOpen(true);
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!categoryFormData.name.trim()) {
      setAlertModal({
        isOpen: true,
        message: "Kategori adı zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      if (editingCategory) {
        await updateStockCategory(editingCategory.id, {
          id: editingCategory.id,
          ...categoryFormData,
          isActive: true,
        });
        setAlertModal({
          isOpen: true,
          message: "Kategori başarıyla güncellendi!",
          type: "success",
        });
      } else {
        await createStockCategory(categoryFormData);
        setAlertModal({
          isOpen: true,
          message: "Kategori başarıyla oluşturuldu!",
          type: "success",
        });
      }
      setIsCategoryModalOpen(false);
      loadData();
    } catch (error) {
      console.error("Kategori kaydedilemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Kategori kaydedilirken hata oluştu!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = (category: StockCategory) => {
    setConfirmModal({
      isOpen: true,
      message: `"${category.name}" kategorisini silmek istediğinize emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await deleteStockCategory(category.id);
          setAlertModal({
            isOpen: true,
            message: "Kategori başarıyla silindi!",
            type: "success",
          });
          loadData();
        } catch (error) {
          console.error("Kategori silinemedi:", error);
          setAlertModal({
            isOpen: true,
            message: "Kategori silinirken hata oluştu!",
            type: "error",
          });
        } finally {
          setLoading(false);
          setConfirmModal({ ...confirmModal, isOpen: false });
        }
      },
    });
  };

  // ==================== UNIT CRUD ====================
  const openAddUnitModal = () => {
    setEditingUnit(null);
    setUnitFormData({ unitName: "", abbreviation: "", sortOrder: 0 });
    setIsUnitModalOpen(true);
  };

  const openEditUnitModal = (unit: StockUnit) => {
    setEditingUnit(unit);
    setUnitFormData({
      unitName: unit.unitName,
      abbreviation: unit.abbreviation || "",
      sortOrder: unit.sortOrder || 0,
    });
    setIsUnitModalOpen(true);
  };

  const handleUnitSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!unitFormData.unitName.trim()) {
      setAlertModal({
        isOpen: true,
        message: "Birim adı zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      if (editingUnit) {
        await updateStockUnit(editingUnit.id, {
          ...unitFormData,
          id: editingUnit.id,
        });
        setAlertModal({
          isOpen: true,
          message: "Birim başarıyla güncellendi!",
          type: "success",
        });
      } else {
        await createStockUnit(unitFormData);
        setAlertModal({
          isOpen: true,
          message: "Birim başarıyla oluşturuldu!",
          type: "success",
        });
      }
      setIsUnitModalOpen(false);
      loadData();
    } catch (error) {
      console.error("Birim kaydedilemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Birim kaydedilirken hata oluştu!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUnit = (unit: StockUnit) => {
    setConfirmModal({
      isOpen: true,
      message: `"${unit.unitName}" birimini silmek istediğinize emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await deleteStockUnit(unit.id);
          setAlertModal({
            isOpen: true,
            message: "Birim başarıyla silindi!",
            type: "success",
          });
          loadData();
        } catch (error) {
          console.error("Birim silinemedi:", error);
          setAlertModal({
            isOpen: true,
            message: "Birim silinirken hata oluştu!",
            type: "error",
          });
        } finally {
          setLoading(false);
          setConfirmModal({ ...confirmModal, isOpen: false });
        }
      },
    });
  };

  // ==================== MOVEMENT CRUD ====================
  const openAddMovementModal = (stock?: Stock) => {
    const userName = user ? `${user.firstName} ${user.lastName}` : "";

    setMovementFormData({
      stockId: stock?.id || "",
      movementType: StockMovementType.Giris,
      quantity: 0,
      unitPrice: stock?.unitPrice || 0,
      totalPrice: 0,
      currency: stock?.currency || "TRY",
      referenceNo: "",
      referenceType: "",
      description: "",
      movementDate: new Date().toISOString().slice(0, 16),
      movedBy: userName,
      fromWarehouse: stock?.warehouseLocation || "",
      toWarehouse: "",
      relatedOrderId: undefined,
    });
    setIsMovementModalOpen(true);
  };

  const handleMovementSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!movementFormData.stockId || movementFormData.quantity <= 0) {
      setAlertModal({
        isOpen: true,
        message: "Stok ve miktar seçimi zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      await createStockMovement({
        ...movementFormData,
        totalPrice: movementFormData.quantity * movementFormData.unitPrice,
      });
      setAlertModal({
        isOpen: true,
        message: "Stok hareketi başarıyla kaydedildi!",
        type: "success",
      });
      setIsMovementModalOpen(false);
      loadData();
      if (activeTab === "movements") {
        loadMovements();
      }
    } catch (error) {
      console.error("Hareket kaydedilemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Stok hareketi kaydedilirken hata oluştu!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Stock detail view
  const openStockDetail = async (stock: Stock) => {
    setSelectedStockForDetail(stock);
    try {
      const response = await getStockMovementsByStock(stock.id);
      setStockMovements(response.data || []);
    } catch (error) {
      console.error("Hareketler yüklenemedi:", error);
      setStockMovements([]);
    }
    setIsStockDetailModalOpen(true);
  };

  // Filtered stocks
  const filteredStocks = stocks.filter((stock) => {
    const matchesSearch =
      turkishIncludes(stock.productCode, searchTerm) ||
      turkishIncludes(stock.productName, searchTerm) ||
      turkishIncludes(stock.description || "", searchTerm) ||
      turkishIncludes(stock.barcode || "", searchTerm);
    return matchesSearch;
  });

  // Filtered categories
  const filteredCategories = categories.filter((cat) =>
    turkishIncludes(cat.name, searchTerm),
  );

  // Filtered units
  const filteredUnits = units.filter((unit) =>
    turkishIncludes(unit.unitName || "", searchTerm),
  );

  if (loading && stocks.length === 0) {
    return <PageLoader message="Stok verileri yükleniyor..." />;
  }

  return (
    <div className="stock-management-container">
      {/* Header */}
      <div className="stock-header">
        <div className="stock-header-content">
          <h1>
            <span className="stock-header-icon">📦</span>
            Stok Yönetimi
          </h1>
          <p className="stock-subtitle">
            Stok kategorileri, ürünler ve hareketleri yönetin
          </p>
        </div>
        <div className="stock-header-actions">
          {activeTab === "stocks" && (
            <button className="stock-add-button" onClick={openAddStockModal}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Yeni Stok
            </button>
          )}
          {activeTab === "categories" && (
            <button className="stock-add-button" onClick={openAddCategoryModal}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Yeni Kategori
            </button>
          )}
          {activeTab === "units" && (
            <button className="stock-add-button" onClick={openAddUnitModal}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Yeni Birim
            </button>
          )}
          {activeTab === "movements" && (
            <button
              className="stock-add-button"
              onClick={() => openAddMovementModal()}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Yeni Hareket
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="stock-tabs">
        <button
          className={`stock-tab ${activeTab === "stocks" ? "active" : ""}`}
          onClick={() => setActiveTab("stocks")}
        >
          📦 Stoklar
        </button>
        <button
          className={`stock-tab ${activeTab === "categories" ? "active" : ""}`}
          onClick={() => setActiveTab("categories")}
        >
          📁 Kategoriler
        </button>
        <button
          className={`stock-tab ${activeTab === "units" ? "active" : ""}`}
          onClick={() => setActiveTab("units")}
        >
          📏 Birimler
        </button>
        <button
          className={`stock-tab ${activeTab === "movements" ? "active" : ""}`}
          onClick={() => setActiveTab("movements")}
        >
          📊 Hareketler
        </button>
        <button
          className={`stock-tab ${activeTab === "lowstock" ? "active" : ""}`}
          onClick={() => setActiveTab("lowstock")}
        >
          ⚠️ Düşük Stok
        </button>
      </div>

      {/* Search & Filters */}
      {(activeTab === "stocks" ||
        activeTab === "categories" ||
        activeTab === "units") && (
        <div className="stock-search-bar">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder={
              activeTab === "stocks"
                ? "Stok ara (kod, ad, barkod)..."
                : "Kategori ara..."
            }
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {activeTab === "stocks" && (
            <select
              value={stockTypeFilter}
              onChange={(e) => handleStockTypeFilter(e.target.value)}
              className="stock-type-filter"
            >
              <option value="all">Tüm Tipler</option>
              <option value="Hammadde">Hammadde</option>
              <option value="YariMamul">Yarı Mamül</option>
              <option value="Mamul">Mamül</option>
            </select>
          )}
        </div>
      )}

      {/* Content */}
      <div className="stock-content">
        {/* Stocks Tab */}
        {activeTab === "stocks" && (
          <div className="stock-grid">
            {filteredStocks.length === 0 ? (
              <div className="stock-empty">
                <p>Henüz stok kaydı bulunmuyor.</p>
              </div>
            ) : (
              filteredStocks.map((stock) => (
                <div key={stock.id} className="stock-card">
                  <div className="stock-card-header">
                    <span
                      className={`stock-type-badge ${StockType[stock.stockType || StockType.Hammadde].toLowerCase()}`}
                    >
                      {stockTypeLabels[stock.stockType || StockType.Hammadde]}
                    </span>
                    <span className="stock-code">{stock.productCode}</span>
                  </div>
                  <h3 className="stock-name">{stock.productName}</h3>
                  {stock.description && (
                    <p className="stock-description">{stock.description}</p>
                  )}
                  <div className="stock-info">
                    <div className="stock-info-row">
                      <span className="stock-info-label">Mevcut Stok:</span>
                      <span
                        className={`stock-info-value ${stock.currentStock <= (stock.minimumStock || 0) ? "low" : ""}`}
                      >
                        {stock.currentStock} {stock.unit}
                      </span>
                    </div>
                    <div className="stock-info-row">
                      <span className="stock-info-label">Birim Fiyat:</span>
                      <span className="stock-info-value">
                        {stock.unitPrice?.toLocaleString("tr-TR", {
                          minimumFractionDigits: 2,
                        })}{" "}
                        {stock.currency}
                      </span>
                    </div>
                    {stock.warehouseLocation && (
                      <div className="stock-info-row">
                        <span className="stock-info-label">Depo:</span>
                        <span className="stock-info-value">
                          {stock.warehouseLocation}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="stock-card-actions">
                    <button
                      className="stock-action-btn view"
                      onClick={() => openStockDetail(stock)}
                      title="Detay"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    </button>
                    <button
                      className="stock-action-btn movement"
                      onClick={() => openAddMovementModal(stock)}
                      title="Hareket Ekle"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M12 5v14M5 12h14"></path>
                      </svg>
                    </button>
                    <button
                      className="stock-action-btn edit"
                      onClick={() => openEditStockModal(stock)}
                      title="Düzenle"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className="stock-action-btn delete"
                      onClick={() => handleDeleteStock(stock)}
                      title="Sil"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === "categories" && (
          <div className="category-grid">
            {filteredCategories.length === 0 ? (
              <div className="stock-empty">
                <p>Henüz kategori bulunmuyor.</p>
              </div>
            ) : (
              filteredCategories.map((category) => (
                <div key={category.id} className="category-card">
                  <div className="category-card-header">
                    <h3>{category.name}</h3>
                    <span
                      className={`category-status ${category.isActive ? "active" : "inactive"}`}
                    >
                      {category.isActive ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                  {category.description && (
                    <p className="category-description">
                      {category.description}
                    </p>
                  )}
                  <div className="category-card-actions">
                    <button
                      className="stock-action-btn edit"
                      onClick={() => openEditCategoryModal(category)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className="stock-action-btn delete"
                      onClick={() => handleDeleteCategory(category)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Units Tab */}
        {activeTab === "units" && (
          <div className="category-grid">
            {filteredUnits.length === 0 ? (
              <div className="stock-empty">
                <p>Henüz birim bulunmuyor.</p>
              </div>
            ) : (
              filteredUnits.map((unit) => (
                <div key={unit.id} className="category-card">
                  <div className="category-card-header">
                    <h3>
                      {unit.unitName ||
                        (unit as any).name ||
                        (unit as any).UnitName ||
                        "Birim"}
                    </h3>
                    <span
                      className={`category-status ${unit.isActive ? "active" : "inactive"}`}
                    >
                      {unit.isActive ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                  {unit.abbreviation && (
                    <p className="category-description">
                      Kısaltma: {unit.abbreviation}
                    </p>
                  )}
                  {unit.sortOrder !== undefined && (
                    <p className="category-description">
                      Sıra: {unit.sortOrder}
                    </p>
                  )}
                  <div className="category-card-actions">
                    <button
                      className="stock-action-btn edit"
                      onClick={() => openEditUnitModal(unit)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className="stock-action-btn delete"
                      onClick={() => handleDeleteUnit(unit)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Movements Tab */}
        {activeTab === "movements" && (
          <div className="movements-table-container">
            <table className="movements-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Stok</th>
                  <th>Hareket Tipi</th>
                  <th>Miktar</th>
                  <th>Birim Fiyat</th>
                  <th>Toplam</th>
                  <th>Referans</th>
                  <th>Açıklama</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="empty-row">
                      Henüz hareket kaydı bulunmuyor.
                    </td>
                  </tr>
                ) : (
                  movements.map((movement) => (
                    <tr key={movement.id}>
                      <td>
                        {new Date(movement.movementDate).toLocaleDateString(
                          "tr-TR",
                        )}
                      </td>
                      <td>
                        {movement.stock?.productName ||
                          `Stok #${movement.stockId}`}
                      </td>
                      <td>
                        <span
                          className="movement-type-badge"
                          style={{
                            backgroundColor:
                              movementTypeColors[movement.movementType],
                          }}
                        >
                          {movementTypeLabels[movement.movementType]}
                        </span>
                      </td>
                      <td>{movement.quantity}</td>
                      <td>
                        {movement.unitPrice?.toLocaleString("tr-TR", {
                          minimumFractionDigits: 2,
                        })}{" "}
                        {movement.currency}
                      </td>
                      <td>
                        {movement.totalPrice?.toLocaleString("tr-TR", {
                          minimumFractionDigits: 2,
                        })}{" "}
                        {movement.currency}
                      </td>
                      <td>{movement.referenceNo || "-"}</td>
                      <td>{movement.description || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Low Stock Tab */}
        {activeTab === "lowstock" && (
          <div className="low-stock-grid">
            {lowStockItems.length === 0 ? (
              <div className="stock-empty success">
                <p>✅ Tüm stoklar yeterli seviyede!</p>
              </div>
            ) : (
              lowStockItems.map((stock) => (
                <div key={stock.id} className="stock-card low-stock">
                  <div className="stock-card-header">
                    <span className="stock-type-badge warning">
                      ⚠️ Düşük Stok
                    </span>
                    <span className="stock-code">{stock.productCode}</span>
                  </div>
                  <h3 className="stock-name">{stock.productName}</h3>
                  <div className="stock-info">
                    <div className="stock-info-row">
                      <span className="stock-info-label">Mevcut:</span>
                      <span className="stock-info-value low">
                        {stock.currentStock} {stock.unit}
                      </span>
                    </div>
                    <div className="stock-info-row">
                      <span className="stock-info-label">Minimum:</span>
                      <span className="stock-info-value">
                        {stock.minimumStock} {stock.unit}
                      </span>
                    </div>
                    <div className="stock-info-row">
                      <span className="stock-info-label">Eksik:</span>
                      <span className="stock-info-value low">
                        {(stock.minimumStock || 0) - stock.currentStock}{" "}
                        {stock.unit}
                      </span>
                    </div>
                  </div>
                  <div className="stock-card-actions">
                    <button
                      className="stock-action-btn movement"
                      onClick={() => openAddMovementModal(stock)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M12 5v14M5 12h14"></path>
                      </svg>
                      Stok Girişi Yap
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Stock Modal */}
      {isStockModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsStockModalOpen(false)}
        >
          <div
            className="modal-content large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{editingStock ? "Stok Düzenle" : "Yeni Stok Ekle"}</h2>
              <button
                className="modal-close"
                onClick={() => setIsStockModalOpen(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleStockSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Ürün Kodu *</label>
                  <input
                    type="text"
                    value={stockFormData.productCode}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        productCode: e.target.value,
                      })
                    }
                    placeholder="Örn: KMS-001"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Ürün Adı *</label>
                  <input
                    type="text"
                    value={stockFormData.productName}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        productName: e.target.value,
                      })
                    }
                    placeholder="Ürün adını girin"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Kategori</label>
                  <select
                    value={stockFormData.categoryId}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        categoryId: e.target.value,
                      })
                    }
                  >
                    <option value="">Kategori Seçin</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Stok Tipi</label>
                  <select
                    value={stockFormData.stockType}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        stockType: Number(e.target.value),
                      })
                    }
                  >
                    <option value={StockType.Hammadde}>Hammadde</option>
                    <option value={StockType.YariMamul}>Yarı Mamül</option>
                    <option value={StockType.Mamul}>Mamül</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  value={stockFormData.description}
                  onChange={(e) =>
                    setStockFormData({
                      ...stockFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Ürün açıklaması"
                  rows={2}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Birim</label>
                  <select
                    value={stockFormData.unit}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        unit: e.target.value,
                      })
                    }
                  >
                    <option value="">Birim Seçin</option>
                    {units.map((unit) => (
                      <option
                        key={unit.id}
                        value={
                          unit.unitName ||
                          (unit as any).name ||
                          (unit as any).UnitName
                        }
                      >
                        {unit.unitName ||
                          (unit as any).name ||
                          (unit as any).UnitName ||
                          "Birim"}
                        {unit.abbreviation ? ` (${unit.abbreviation})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Mevcut Stok</label>
                  <input
                    type="number"
                    value={stockFormData.currentStock}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        currentStock: Number(e.target.value),
                      })
                    }
                    min={0}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Minimum Stok</label>
                  <input
                    type="number"
                    value={stockFormData.minimumStock}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        minimumStock: Number(e.target.value),
                      })
                    }
                    min={0}
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Maksimum Stok</label>
                  <input
                    type="number"
                    value={stockFormData.maximumStock}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        maximumStock: Number(e.target.value),
                      })
                    }
                    min={0}
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Birim Fiyat</label>
                  <input
                    type="number"
                    value={stockFormData.unitPrice}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        unitPrice: Number(e.target.value),
                      })
                    }
                    min={0}
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Para Birimi</label>
                  <select
                    value={stockFormData.currency}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        currency: e.target.value,
                      })
                    }
                  >
                    <option value="TRY">TRY - Türk Lirası</option>
                    <option value="USD">USD - Amerikan Doları</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - İngiliz Sterlini</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Depo Konumu</label>
                  <input
                    type="text"
                    value={stockFormData.warehouseLocation}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        warehouseLocation: e.target.value,
                      })
                    }
                    placeholder="Örn: A-12-3"
                  />
                </div>
                <div className="form-group">
                  <label>Tedarikçi</label>
                  <input
                    type="text"
                    value={stockFormData.supplier}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        supplier: e.target.value,
                      })
                    }
                    placeholder="Tedarikçi adı"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Renk</label>
                  <input
                    type="text"
                    value={stockFormData.color}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        color: e.target.value,
                      })
                    }
                    placeholder="Ürün rengi"
                  />
                </div>
                <div className="form-group">
                  <label>Boyut/Ebat</label>
                  <input
                    type="text"
                    value={stockFormData.size}
                    onChange={(e) =>
                      setStockFormData({
                        ...stockFormData,
                        size: e.target.value,
                      })
                    }
                    placeholder="Örn: 150 cm en"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Barkod</label>
                <input
                  type="text"
                  value={stockFormData.barcode}
                  onChange={(e) =>
                    setStockFormData({
                      ...stockFormData,
                      barcode: e.target.value,
                    })
                  }
                  placeholder="Barkod numarası"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsStockModalOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="btn-save" disabled={loading}>
                  {loading
                    ? "Kaydediliyor..."
                    : editingStock
                      ? "Güncelle"
                      : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {isCategoryModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsCategoryModalOpen(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? "Kategori Düzenle" : "Yeni Kategori"}</h2>
              <button
                className="modal-close"
                onClick={() => setIsCategoryModalOpen(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCategorySubmit} className="modal-form">
              <div className="form-group">
                <label>Kategori Adı *</label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) =>
                    setCategoryFormData({
                      ...categoryFormData,
                      name: e.target.value,
                    })
                  }
                  placeholder="Kategori adını girin"
                  required
                />
              </div>
              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  value={categoryFormData.description}
                  onChange={(e) =>
                    setCategoryFormData({
                      ...categoryFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Kategori açıklaması"
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsCategoryModalOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="btn-save" disabled={loading}>
                  {loading
                    ? "Kaydediliyor..."
                    : editingCategory
                      ? "Güncelle"
                      : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Unit Modal */}
      {isUnitModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsUnitModalOpen(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUnit ? "Birim Düzenle" : "Yeni Birim"}</h2>
              <button
                className="modal-close"
                onClick={() => setIsUnitModalOpen(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUnitSubmit} className="modal-form">
              <div className="form-group">
                <label>Birim Adı *</label>
                <input
                  type="text"
                  value={unitFormData.unitName}
                  onChange={(e) =>
                    setUnitFormData({
                      ...unitFormData,
                      unitName: e.target.value,
                    })
                  }
                  placeholder="Örn: Metre, Kilogram, Adet"
                  required
                />
              </div>
              <div className="form-group">
                <label>Kısaltma</label>
                <input
                  type="text"
                  value={unitFormData.abbreviation}
                  onChange={(e) =>
                    setUnitFormData({
                      ...unitFormData,
                      abbreviation: e.target.value,
                    })
                  }
                  placeholder="Örn: m, kg, ad"
                />
              </div>
              <div className="form-group">
                <label>Sıralama</label>
                <input
                  type="number"
                  value={unitFormData.sortOrder}
                  onChange={(e) =>
                    setUnitFormData({
                      ...unitFormData,
                      sortOrder: Number(e.target.value),
                    })
                  }
                  placeholder="0"
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsUnitModalOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="btn-save" disabled={loading}>
                  {loading
                    ? "Kaydediliyor..."
                    : editingUnit
                      ? "Güncelle"
                      : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Movement Modal */}
      {isMovementModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsMovementModalOpen(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Stok Hareketi Ekle</h2>
              <button
                className="modal-close"
                onClick={() => setIsMovementModalOpen(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleMovementSubmit} className="modal-form">
              <div className="form-group">
                <label>Stok *</label>
                <select
                  value={movementFormData.stockId}
                  onChange={(e) => {
                    const selectedStock = stocks.find(
                      (s) => s.id === e.target.value,
                    );
                    setMovementFormData({
                      ...movementFormData,
                      stockId: e.target.value,
                      unitPrice: selectedStock?.unitPrice || 0,
                      currency: selectedStock?.currency || "TRY",
                    });
                  }}
                  required
                >
                  <option value="">Stok Seçin</option>
                  {stocks.map((stock) => (
                    <option key={stock.id} value={stock.id}>
                      {stock.productCode} - {stock.productName} (Mevcut:{" "}
                      {stock.currentStock} {stock.unit})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Hareket Tipi *</label>
                  <select
                    value={movementFormData.movementType}
                    onChange={(e) =>
                      setMovementFormData({
                        ...movementFormData,
                        movementType: Number(e.target.value),
                      })
                    }
                  >
                    <option value={StockMovementType.Giris}>Giriş</option>
                    <option value={StockMovementType.Cikis}>Çıkış</option>
                    <option value={StockMovementType.Transfer}>Transfer</option>
                    <option value={StockMovementType.Sayim}>Sayım</option>
                    <option value={StockMovementType.Iade}>İade</option>
                    <option value={StockMovementType.Fire}>Fire</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Miktar *</label>
                  <input
                    type="number"
                    value={movementFormData.quantity}
                    onChange={(e) =>
                      setMovementFormData({
                        ...movementFormData,
                        quantity: Number(e.target.value),
                      })
                    }
                    min={0}
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Birim Fiyat</label>
                  <input
                    type="number"
                    value={movementFormData.unitPrice}
                    onChange={(e) =>
                      setMovementFormData({
                        ...movementFormData,
                        unitPrice: Number(e.target.value),
                      })
                    }
                    min={0}
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Para Birimi</label>
                  <select
                    value={movementFormData.currency}
                    onChange={(e) =>
                      setMovementFormData({
                        ...movementFormData,
                        currency: e.target.value,
                      })
                    }
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Referans No</label>
                  <input
                    type="text"
                    value={movementFormData.referenceNo}
                    onChange={(e) =>
                      setMovementFormData({
                        ...movementFormData,
                        referenceNo: e.target.value,
                      })
                    }
                    placeholder="Örn: IRS-2026-001"
                  />
                </div>
                <div className="form-group">
                  <label>Referans Tipi</label>
                  <input
                    type="text"
                    value={movementFormData.referenceType}
                    onChange={(e) =>
                      setMovementFormData({
                        ...movementFormData,
                        referenceType: e.target.value,
                      })
                    }
                    placeholder="Örn: İrsaliye, Sipariş"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Hareket Tarihi</label>
                  <input
                    type="datetime-local"
                    value={movementFormData.movementDate}
                    onChange={(e) =>
                      setMovementFormData({
                        ...movementFormData,
                        movementDate: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>İşlemi Yapan</label>
                  <input
                    type="text"
                    value={movementFormData.movedBy}
                    onChange={(e) =>
                      setMovementFormData({
                        ...movementFormData,
                        movedBy: e.target.value,
                      })
                    }
                    placeholder="Personel adı"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  value={movementFormData.description}
                  onChange={(e) =>
                    setMovementFormData({
                      ...movementFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Hareket açıklaması"
                  rows={2}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsMovementModalOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="btn-save" disabled={loading}>
                  {loading ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stock Detail Modal */}
      {isStockDetailModalOpen && selectedStockForDetail && (
        <div
          className="modal-overlay"
          onClick={() => setIsStockDetailModalOpen(false)}
        >
          <div
            className="modal-content large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Stok Detayı - {selectedStockForDetail.productName}</h2>
              <button
                className="modal-close"
                onClick={() => setIsStockDetailModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="stock-detail-content">
              <div className="stock-detail-info">
                <div className="detail-row">
                  <span className="detail-label">Ürün Kodu:</span>
                  <span className="detail-value">
                    {selectedStockForDetail.productCode}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Stok Tipi:</span>
                  <span className="detail-value">
                    {
                      stockTypeLabels[
                        selectedStockForDetail.stockType || StockType.Hammadde
                      ]
                    }
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Mevcut Stok:</span>
                  <span className="detail-value">
                    {selectedStockForDetail.currentStock}{" "}
                    {selectedStockForDetail.unit}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Birim Fiyat:</span>
                  <span className="detail-value">
                    {selectedStockForDetail.unitPrice?.toLocaleString("tr-TR")}{" "}
                    {selectedStockForDetail.currency}
                  </span>
                </div>
              </div>

              <h3>Stok Hareketleri</h3>
              <table className="movements-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Hareket Tipi</th>
                    <th>Miktar</th>
                    <th>Referans</th>
                    <th>Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {stockMovements.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="empty-row">
                        Bu stok için hareket kaydı bulunmuyor.
                      </td>
                    </tr>
                  ) : (
                    stockMovements.map((movement) => (
                      <tr key={movement.id}>
                        <td>
                          {new Date(movement.movementDate).toLocaleDateString(
                            "tr-TR",
                          )}
                        </td>
                        <td>
                          <span
                            className="movement-type-badge"
                            style={{
                              backgroundColor:
                                movementTypeColors[movement.movementType],
                            }}
                          >
                            {movementTypeLabels[movement.movementType]}
                          </span>
                        </td>
                        <td>{movement.quantity}</td>
                        <td>{movement.referenceNo || "-"}</td>
                        <td>{movement.description || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default StockManagement;
